import { CalendarCheckIcon, PaperPlaneIcon } from "@App/Components/common/Icons";
import { GarageNotBookablePill } from "@App/Components/GarageListItem/GarageNotBookablePill";
import type { Garage, Organization, Service } from "@App/types";
import { GarageHasAvailableDays } from "@App/utils/conditionals/garages";
import { OrganizationIsArval } from "@App/utils/conditionals/organization";
import type { SupportedCountry } from "@App/utils/country";
import { formatShortDate } from "@App/utils/dates";
import type React from "react";
import type { TFunction } from "react-i18next";
import { GarageCTAButton } from "./GarageCTAButton";

type Props = {
  garage: Garage;
  showAsNonBookable: boolean;
  bookable: boolean;
  service?: Service;
  organization: Organization;
  handleClick: () => void;
  showPhoneNumber: boolean;
  setShowPhoneNumber: (value: boolean) => void;
  t: TFunction;
  showEarliestSlot?: boolean;
  countryCode?: SupportedCountry;
};

export const GarageNextAppointment: React.FC<Props> = ({
  garage,
  showAsNonBookable,
  bookable,
  organization,
  handleClick,
  showPhoneNumber,
  setShowPhoneNumber,
  showEarliestSlot = true,
  countryCode = "DE",
  t,
}) => {
  if (!bookable && !showAsNonBookable) return null;

  const hasAvailability = GarageHasAvailableDays(garage);

  return (
    <div className={`px-5 fourth lg:pl-8 ${bookable && "mb-5"}`}>
      {bookable && !showAsNonBookable && showEarliestSlot && (
        <div className="flex text-base lg:flex-col justify-between mt-2 lg:mt-0">
          <div className="mr-1 font-bold mb-2">
            {OrganizationIsArval(organization) &&
              (hasAvailability ? (
                <CalendarCheckIcon className="inline lg:hidden h-4 w-4 mr-1 -mt-1" />
              ) : (
                <PaperPlaneIcon className="inline lg:hidden h-4 w-5 mr-1 -mt-1" />
              ))}
            {t("garageList.nextAppointment")}
          </div>
          <button type="button" className="font-bold" onClick={handleClick}>
            <div className="lg:w-fit lg:px-3 lg:py-2 lg:rounded lg:bg-primary text-primary lg:text-white underline lg:no-underline cursor-pointer mb-5">
              {hasAvailability
                ? formatShortDate(garage.available_days?.[0]?.available_at ?? "")
                : t("garageList.no-availability")}
            </div>
          </button>
        </div>
      )}
      {/* Mobile CTA */}
      {bookable && !showAsNonBookable && (
        <GarageCTAButton
          label={t("garageList.gotoAppointmentselect")}
          onClick={handleClick}
          className="lg:hidden"
        />
      )}

      {bookable && showAsNonBookable && (
        <div className="flex flex-col lg:gap-4 gap-2 items-start">
          <GarageNotBookablePill text={t("garageList.notBookable")} />
          <div className="lg:block hidden">
            {garage.nationwide_service
              ? t("garageList.mobileService", { context: countryCode })
              : `${garage.street}, ${garage.postcode} ${garage.city}`}
          </div>
        </div>
      )}

      {showAsNonBookable && !showPhoneNumber && (
        <GarageCTAButton
          label={t("garageList.showPhoneNumber")}
          onClick={() => setShowPhoneNumber(true)}
          className="lg:hidden"
        />
      )}

      {showAsNonBookable && showPhoneNumber && (
        <GarageCTAButton
          label={garage.phone}
          href={`tel:${garage.phone}`}
          className="lg:hidden font-serif font-bold"
        />
      )}
    </div>
  );
};
